import { ReactElement } from 'react';

import { GetStaticProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import { DefaultLayout, Footer } from '@/components/layouts';
import { InventoryAll } from '@/components/sections/inventory';
import { PropsBuilder } from '@/graphql/api';
import {
  useGetGlobalQuery,
  useGetInventoryAllQuery,
  useGetInventoryDefinitionsQuery,
  useGetMenuQuery,
} from '@/graphql/generated';
import { ComponentMetaMetadata, GetMenuQuery, InventoryDefinitionEntity } from '@/graphql/generated/graphql';
import { useLocale } from '@/hooks';
import { DEFAULT_LOCALE, LOCALES, PAGE_REVALIDATE_TIME } from '@/utils/constants';
import { assignTitlesToLetters } from '@/utils/helper-functions/assignTitlesToLetters';

import { NextPageWithLayout } from '../_app';

const Inventory: NextPageWithLayout = () => {
  const locale = useLocale();
  const inventoryDefinitions = useGetInventoryDefinitionsQuery({ locale }).data?.inventoryDefinitions
    ?.data! as InventoryDefinitionEntity[];

  const letters = assignTitlesToLetters(inventoryDefinitions);

  return <InventoryAll letters={letters} />;
};

interface Page extends ReactElement {
  props: { metadata: ComponentMetaMetadata; global: Global; menu: GetMenuQuery['menu'] };
}

Inventory.getLayout = (page: Page) => {
  const { menu, metadata } = page.props;

  return (
    <DefaultLayout metadata={metadata} menu={menu}>
      {page}
      <Footer menu={menu} />
    </DefaultLayout>
  );
};

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const propsBuilder = new PropsBuilder();

  propsBuilder.withQuery(useGetGlobalQuery, {
    variables: {
      locale,
    },
  });
  propsBuilder.withQuery(useGetInventoryAllQuery, {
    variables: {
      locale,
    },
    getMetadata: (data) => data.inventoryAll?.data?.attributes?.metadata,
  });
  propsBuilder.withQuery(useGetInventoryDefinitionsQuery, {
    variables: {
      locale,
    },
  });
  propsBuilder.withQuery(useGetMenuQuery, {
    getMenu: (data) => data.menu,
  });

  const lang = await serverSideTranslations(locale || DEFAULT_LOCALE, [LOCALES.COMMON, LOCALES.INVENTORY]);

  return {
    props: {
      ...(await propsBuilder.build(lang)),
    },
    revalidate: PAGE_REVALIDATE_TIME,
  };
};

export default Inventory;
